import * as React from 'react';
import {
  Box,
  Card,
  HeadingText,
  TextAction,
  useClickableCard,
  Icon,
  StackLayout,
  GDSIconSource,
  VisuallyHidden,
  focusOutlineOuter,
} from '@leagueplatform/genesis-core';
import { useUniqueId } from '@leagueplatform/web-common';
import { InternalOrExternalLink as Link } from '@leagueplatform/web-common-components';

export const NODE_TYPE_SPENDING_ACCOUNT_CARD = 'spendingAccountCard';

export type SpendingAccountCardProps = {
  accountName?: string;
  amount?: string;
  ctaText?: string;
  icon?: { icon?: GDSIconSource | string; description?: string };
  title?: string;
  trailingIcon?: { icon?: GDSIconSource | string; description?: string };
  onClick?: (payload: any) => void;
  href?: string;
  isLink?: boolean;
};

const CARD_MAX_WIDTH = '369px';

export function SpendingAccountCard({
  amount,
  ctaText,
  icon,
  trailingIcon,
  title,
  onClick: handleOnClick,
  href = '',
  isLink,
  accountName,
}: SpendingAccountCardProps) {
  const { onCardMouseDown, onCardMouseUp, clickAreaRef, primaryActionRef } =
    useClickableCard();
  const uniqueId = useUniqueId({
    prefix: 'spending-account-card',
  });

  if (!amount && !ctaText && !title) {
    return null;
  }

  const trailingIconId =
    trailingIcon?.description && `${uniqueId}-trailing-icon`;
  const hasAmountOnly = !ctaText && amount;

  return (
    <Card.Elevated
      backgroundColor="secondary"
      css={{
        maxWidth: CARD_MAX_WIDTH,
        width: '100%',
        '&:focus-within': {
          ...focusOutlineOuter,
        },
      }}
    >
      <Card.ClickArea
        onCardMouseDown={onCardMouseDown}
        onCardMouseUp={onCardMouseUp}
        ref={clickAreaRef}
      >
        <Card.Section
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <StackLayout spacing="$half">
            {icon?.icon && <Icon icon={icon.icon} label={icon.description} />}
            {title && (
              <HeadingText size="xs" level="display" as="p">
                {title}
                {amount && (
                  <HeadingText size="xl" level="display">
                    {amount}
                  </HeadingText>
                )}
              </HeadingText>
            )}
            {hasAmountOnly && (
              <HeadingText size="xl" level="display" as="p">
                {amount}
              </HeadingText>
            )}
            {ctaText && (
              <TextAction
                aria-describedby={trailingIconId}
                as="button"
                ref={primaryActionRef}
                onClick={handleOnClick}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(isLink && {
                  as: Link,
                  href,
                })}
                css={{
                  '&[class*="GDS"]:focus, &[class*="GDS"]:focus': {
                    outline: 'none',
                  },
                }}
              >
                {ctaText}
                {accountName && <VisuallyHidden> {accountName}</VisuallyHidden>}
              </TextAction>
            )}
          </StackLayout>
          {trailingIcon?.icon && (
            <Box>
              <Icon
                id={trailingIconId}
                icon={trailingIcon.icon}
                label={trailingIcon?.description}
              />
            </Box>
          )}
        </Card.Section>
      </Card.ClickArea>
    </Card.Elevated>
  );
}
